import * as React from 'react'

import {
  Box,
  Button,
  type LabeledInputProps,
  Text,
  useConfirmationDialog,
} from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import {
  CancelEmailChangeDocument,
  CancelEmailChangeMutation,
  ProfilePageDocument,
} from 'generated/resources/graphql'
import { reaction } from 'mobx'

import { observer } from 'mobx-react'

import { useMutation } from 'modules/core/hooks/useMutation'
import InputText from 'modules/core/components/Inputs/Text'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ProfileEmployeeData } from 'modules/profile/components/ProfileForm/types'

interface Props {
  form: Form
  employeeData: ProfileEmployeeData
  employeeExists: boolean
  isCurrentUser: boolean
  canEditEmployeeEmail?: boolean
  readonly?: boolean
  autofocus?: boolean
  mandatory?: boolean
  secondaryLabel?: LabeledInputProps['secondaryLabel']
}

const Email = ({
  employeeData,
  form,
  readonly,
  employeeExists,
  isCurrentUser,
  canEditEmployeeEmail,
  secondaryLabel,
  mandatory,
  autofocus,
}: Props) => {
  const [cancelEmailChange] = useMutation(CancelEmailChangeDocument, {
    extractorFn: (data: CancelEmailChangeMutation) =>
      data.authenticationMutations.cancelAuthenticationEmailChange,
    refetchQueries: [ProfilePageDocument],
  })

  const { openDialog } = useConfirmationDialog()

  const isReadOnly = React.useMemo(() => {
    const canNotEdit = employeeExists
      ? !canEditEmployeeEmail
      : Boolean(!isCurrentUser && employeeData.joined)

    return readonly || canNotEdit
  }, [employeeData, readonly, employeeExists, canEditEmployeeEmail, isCurrentUser])

  React.useEffect(() => {
    const handler = reaction(
      () => employeeData.unconfirmedEmail,
      () => {
        const email = form.get('email')
        email.set(employeeData.loginEmail || '')
        email.clean()

        const uncomfirmedEmail = form.get('unconfirmed_email')
        uncomfirmedEmail.set(employeeData.unconfirmedEmail)
        uncomfirmedEmail.clean()
      },
      {
        fireImmediately: true,
      }
    )

    return () => {
      if (handler) {
        handler()
      }
    }
  }, [employeeData, form])

  const renderResendInfo = () => {
    const userIsInvited = !!employeeData.access.invitedOn
    const unconfirmedEmail = employeeData.unconfirmedEmail
    const equalEmails = unconfirmedEmail === employeeData.access.email

    if (isReadOnly || !userIsInvited || !unconfirmedEmail || equalEmails) {
      return null
    }

    return (
      <>
        <Text size="200">
          {i18n.t('employee_form.unconfirmed_description', {
            email: unconfirmedEmail,
          })}
        </Text>
        <Button
          type="secondary"
          onClick={() => {
            openDialog({
              title: i18n.t('employee_form.cancel_email_change'),
              description: i18n.t('employee_form.cancel_email_change_confirmation'),
              confirmLabel: i18n.t('employee_form.cancel_email_change'),
              onConfirm: async () => {
                const res = await cancelEmailChange({
                  variables: { employeeId: employeeData.id },
                })
                const result = handleResult(res, (errors) => {
                  app.setFlash({
                    type: 'wrong',
                    message: errors['messages'] || i18n.t('errors.generic_error'),
                  })
                })

                if (result.success) {
                  app.setFlash({
                    type: 'correct',
                    message: i18n.t('employee_form.cancel_email_change'),
                  })
                }
              },
            })
          }}
        >
          {i18n.t('employee_form.cancel_email_change')}
        </Button>
      </>
    )
  }

  const bangText: boolean | undefined = isReadOnly || mandatory

  return (
    <Box gap="s8">
      <InputText
        readonly={isReadOnly}
        field={form.get('email')}
        value={form.get('email').value}
        label={i18n.t('employee_form.email')}
        mandatory={bangText}
        autofocus={autofocus}
        secondaryLabel={secondaryLabel}
      />
      {renderResendInfo()}
    </Box>
  )
}

export default observer(Email)
